import { useRouter } from 'next/router'
import { useEffect, useMemo } from 'react'
import { useGTMBasicPageViewEventWithProperties } from '../../../../helpers/gtmHelper'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import { STUDENT_PAGE_VIEW_ID } from '../../../../modules/ids/gtm/student'
import { buildSanityImage } from '../../../../modules/utils'
import CenterContainer from '../../../common/CenterContainer'
import LandingNav from '../../navigation/LandingNav'
import MobileLandingNav from '../../navigation/LandingNav/MobileLandingNav'
import Footer from '../../section/footer'
import { IPost, MENU_ID } from '../type'
import BlogDetailBackButtonSection from './BlogDetailBackButtonSection'
import BlogDetailDesktopBannerSection from './BlogDetailDesktopBannerSection'
import BlogDetailDesktopCTASection from './BlogDetailDesktopCTASection'
import BlogDetailDesktopMainImageSection from './BlogDetailDesktopMainImageSection'
import BlogDetailDesktopRecommendSection from './BlogDetailDesktopRecommendSection'
import BlogDetailDesktopTitleSection from './BlogDetailDesktopTitleSection'
import BlogDetailMobileBannerSection from './BlogDetailMobileBannerSection'
import BlogDetailMobileCTASection from './BlogDetailMobileCTASection'
import BlogDetailMobileMainImageSection from './BlogDetailMobileMainImageSection'
import BlogDetailMobileRecommendSection from './BlogDetailMobileRecommendSection'
import BlogDetailMobileTitleSection from './BlogDetailMobileTitleSection'
import BlogDetailPortableTextSection from './BlogDetailPortableTextSection'
import BlogDetailTagsSharingSection from './BlogDetailTagsSharingSection'

interface BlogDetailResponsiveProps {
  post: IPost
  isWebviewInitialValue?: boolean
}

const BlogDetailResponsive = ({ post, isWebviewInitialValue }: BlogDetailResponsiveProps) => {
  const { asPath, query } = useRouter()
  const isApp = query.app == 'true'
  const isWebivew = useIsWebview(isWebviewInitialValue)
  const sendGTMPVEventMobile = useGTMBasicPageViewEventWithProperties({
    page_id: STUDENT_PAGE_VIEW_ID.LANDING_BLOG_DETAIL,
    page_title: asPath,
    device_type: 'mobile_web',
  })

  const sendGTMPVEventPc = useGTMBasicPageViewEventWithProperties({
    page_id: STUDENT_PAGE_VIEW_ID.LANDING_BLOG_DETAIL,
    page_title: asPath,
    device_type: 'pc_web',
  })

  useEffect(() => {
    sendGTMPVEventMobile()
    sendGTMPVEventPc()
  }, [])

  const isNotice = post.menuForm === MENU_ID.NOTICE
  const isFAQ = post.menuForm === MENU_ID.FAQ
  const isShortForm = isFAQ || isNotice
  const isNewPortal = useMemo(() => asPath?.includes('/portal/'), [asPath])
  const isForB2BPost = post?.isForB2BPost

  return (
    <div className="text-black">
      {/* Navigation Bar V2*/}
      <div className="sm:block hidden">{!isNewPortal && <LandingNav />}</div>
      <div className="block sm:hidden">{!isWebivew && !isApp && !isNewPortal && <MobileLandingNav />}</div>

      <CenterContainer clx="max-w-[668px] mt-[40px] px-[0px] leading-[25.6px] lists-class text-black">
        <div className="sm:block hidden">
          <BlogDetailDesktopTitleSection post={post} noBackButton={isNewPortal} />
          {!isShortForm && <BlogDetailDesktopMainImageSection post={post} />}
        </div>
        <div className="block sm:hidden">
          <BlogDetailMobileTitleSection post={post} isWebivew={isWebivew} />
          {!isShortForm && <BlogDetailMobileMainImageSection post={post} />}
        </div>
        <div className="sm:px-[0px] px-[16px]">
          <BlogDetailPortableTextSection value={post.content} />
        </div>
        {!isShortForm && (
          <>
            {isForB2BPost ? (
              <></>
            ) : (
              <>
                <div className="sm:block hidden">
                  <BlogDetailDesktopCTASection ctaList={post.ctaList} title={post.ctaSection.ctaTitle} />
                </div>
                <div className="block sm:hidden">
                  {!isWebivew && <BlogDetailMobileCTASection ctaList={post.ctaList} title={post.ctaSection.ctaTitle} />}
                </div>
              </>
            )}
            <div className="sm:block hidden">
              <BlogDetailTagsSharingSection
                tagList={post.tagList}
                title={post.title}
                subtitle={post.subtitle}
                isMobile={false}
                imgUrl={
                  post.thumbnail && Object.keys(post.thumbnail).length > 0 ? buildSanityImage(post.thumbnail).url() : ''
                }
              />
            </div>
            <div className="block sm:hidden">
              <BlogDetailTagsSharingSection
                tagList={post.tagList}
                title={post.title}
                subtitle={post.subtitle}
                isMobile={true}
                imgUrl={buildSanityImage(post.thumbnail).url()}
              />
            </div>
          </>
        )}
        <div className="sm:block hidden">{isShortForm && <BlogDetailBackButtonSection isMobile={false} />}</div>
        <div className="block sm:hidden">{isShortForm && <BlogDetailBackButtonSection isMobile={true} />}</div>
      </CenterContainer>
      <div className="sm:block hidden">
        {!isShortForm && (
          <>
            <BlogDetailDesktopRecommendSection recommendList={post.recommendList} />
            <BlogDetailDesktopBannerSection />
          </>
        )}
        {!isNewPortal && <Footer />}
      </div>
      <div className="block sm:hidden">
        {!isShortForm && <BlogDetailMobileRecommendSection recommendList={post.recommendList} />}
        {!isWebivew && !isShortForm && <BlogDetailMobileBannerSection />}
        {!isWebivew && !isNewPortal && <Footer />}
      </div>
    </div>
  )
}

export default BlogDetailResponsive
